import * as userApi from "../users/user-api"
import doublet from "../utils/doublet"
import React from "react"
import Button from "@material-ui/core/Button"
import FormLabel from "@material-ui/core/FormLabel"
import Paper from "@material-ui/core/Paper"
import Typography from "@material-ui/core/Typography"
import FormikInput from "../components/FormikInput"
import CircularProgress from "@material-ui/core/CircularProgress"
import { Alert, AlertTitle } from "@material-ui/lab"
import { useI18n } from "../hooks"
import { StringMap } from "../types/common"
import { useAuthUser } from "../users/hooks/use-auth-user"
import { makeStyles, Theme } from "@material-ui/core"
import { Formik, Form } from "formik"
import { Link, useHistory, useLocation } from "react-router-dom"
import { paths } from "../paths"
import { useAppDispatch } from "../store"
import { FetchUser } from "../users/store/actions"
import { FetchUserInvitations } from "../invitations/store/actions"
import { Check } from "react-feather"
import { unwrapResult } from "@reduxjs/toolkit"
import { useUrlWithContext } from "../hooks/use-url-with-context"

type Status = "idle" | "verifying" | "verified" | "rejected"

const verificationState = {
  status: "idle" as Status,
  error: null as string | null,
}

const useAccountVerification = () => {
  const [state, setState] = React.useState(verificationState)
  const { user } = useAuthUser()
  const dispatch = useAppDispatch()
  const history = useHistory()
  const location = useLocation()
  const { createPathWithGivenContext } = useUrlWithContext()
  const homePath = paths.home()

  const onVerifyAccount = async (code: string) => {
    setState({ ...state, status: "verifying" })
    try {
      if (user) {
        const [apiError] = await doublet(userApi.verifyAccount, { user: user.id, code })
        if (apiError) throw new Error(apiError.message)
        setState({ ...state, status: "verified" })
      } else {
        throw new Error("User not found")
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error"
      setState({ ...state, status: "rejected", error: errorMessage })
    }
  }

  React.useEffect(() => {
    async function fetchDataAndRedirect() {
      const userInvitationsResult = await dispatch(FetchUserInvitations())
      const userInvitations = unwrapResult(userInvitationsResult)
      if (userInvitations.invitations.length > 0) {
        const userInvitedPath = createPathWithGivenContext({ path: paths.userInvited(), mainContextId: user?.id })
        history.push(userInvitedPath, { from: location })
      } else {
        await dispatch(FetchUser())
      }
    }

    if (state.status === "verified") {
      fetchDataAndRedirect()
    }
  }, [state.status, dispatch, homePath, history, location, user?.id, createPathWithGivenContext])

  return {
    onVerifyAccount,
    error: state.error,
    isVerified: state.status === "verified",
  }
}

const AccountVerificationRoute = () => {
  const classes = useStyles()
  const translations = useTranslations(defaultTranslations)
  const { user } = useAuthUser()
  const { createPathWithGivenContext } = useUrlWithContext()
  const { onVerifyAccount, error, isVerified } = useAccountVerification()

  const formikProps = {
    initialValues: { code: "" },
    onSubmit: ({ code }: { code: string }) => onVerifyAccount(code),
  }

  if (isVerified) {
    return (
      <Paper style={{ padding: "40px 32px 64px 32px", textAlign: "center" }} variant="outlined" elevation={0}>
        <div className={classes.iconWrapper}>
          <Check size={80} color="green" />
        </div>
        <Typography variant="h4" gutterBottom>
          {translations.accountVerified}
        </Typography>
      </Paper>
    )
  }

  return (
    <div className={classes.container}>
      <Typography variant="h5" className={classes.title} data-test="welcome-header">
        {translations.pageTitle}
        {user?.firstname}
      </Typography>
      <Typography variant="body1" component="p" color="textSecondary" className={classes.subtitle}>
        {translations.pageSubtitle}
        <strong>{user?.email}</strong>
      </Typography>
      <Formik {...formikProps}>
        {({ errors, values, isSubmitting }) => {
          const shouldDisableButton = Boolean(values.code === "" || errors.code || isSubmitting)
          return (
            <Form>
              <FormLabel className={classes.formLabel}>{translations.inputLabel}</FormLabel>
              <FormikInput
                id="code"
                name="code"
                variant="outlined"
                disabled={isSubmitting}
                autoComplete="off"
                className={classes.formInput}
                fullWidth
              />
              {error && (
                <div className={classes.formError}>
                  <Alert severity={"error"}>
                    <AlertTitle>{translations.errorTitle}</AlertTitle>
                    {error ? error : translations.verificationFailedMsg}
                  </Alert>
                </div>
              )}
              <div className={classes.formActions}>
                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  startIcon={isSubmitting ? <CircularProgress size={16} color="secondary" /> : null}
                  disabled={shouldDisableButton}
                >
                  {translations.verifyAccountBtnLabel}
                </Button>
                <Button
                  color="primary"
                  component={Link}
                  to={createPathWithGivenContext({ path: paths.newVerificationCode(), mainContextId: user?.id })}
                >
                  {translations.noCodeBtnLabel}
                </Button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

const useTranslations = (defaults: Translations = defaultTranslations): Translations => {
  const { translations: t } = useI18n("translation")
  const accountVerificationPageTranslations = (t?.accountVerificationPage || {}) as StringMap
  const {
    inputLabel = defaults.inputLabel,
    errorTitle = defaults.errorTitle,
    pageTitle = defaults.pageTitle,
    pageSubtitle = defaults.pageSubtitle,
    verificationFailedMsg = defaults.verificationFailedMsg,
    verifyAccountBtnLabel = defaults.verifyAccountBtnLabel,
    noCodeBtnLabel = defaults.noCodeBtnLabel,
  } = accountVerificationPageTranslations

  return {
    inputLabel,
    errorTitle,
    pageTitle,
    pageSubtitle,
    verificationFailedMsg,
    verifyAccountBtnLabel,
    noCodeBtnLabel,
    accountVerified: t.accountVerified || defaults.accountVerified,
  }
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginTop: theme.spacing(2),
    width: "100%",
  },
  title: {
    paddingBottom: theme.spacing(1.5),
  },
  subtitle: {
    paddingBottom: theme.spacing(4),
    "& strong": {
      textDecoration: "underline",
      color: theme.palette.common.black,
    },
  },
  formLabel: {
    display: "block",
    marginBottom: theme.spacing(1.5),
  },
  formInput: { marginBottom: theme.spacing(4) },
  formError: { marginBottom: theme.spacing(4) },
  formActions: {
    display: "flex",
    "& > :first-child": {
      marginRight: theme.spacing(2),
    },
  },
  iconWrapper: {
    display: "inline-block",
    animation: `$checkIn 400ms ease-out`,
    transformOrigin: "center",
  },
  "@keyframes checkIn": {
    "0%": {
      opacity: 0,
      transform: "scale(0.5) rotate(-90deg)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1) rotate(0deg)",
    },
  },
}))

const defaultTranslations = {
  inputLabel: "Verification code",
  errorTitle: "Verification failed",
  pageTitle: "Welcome ",
  pageSubtitle: "We have sent a verification code to your email ",
  verificationFailedMsg: "Failed to verify account",
  verifyAccountBtnLabel: "Verify account",
  noCodeBtnLabel: "I don't have code",
  accountVerified: "Your account has been verified",
}
type Translations = typeof defaultTranslations

export default AccountVerificationRoute
